import { useCallback, useMemo } from "react";
import useSWR, { useSWRConfig } from "swr";
import { auth, vceFirestore } from "../../configs/FirebaseConfig";
import {
  FIRESTORE_HISTORY_SUBCOLLECTION,
  FIRESTORE_ORDERS_TABLE,
  FIRESTORE_PICK_EVENTS_SUBCOLLECTION,
  FIRESTORE_PICK_WAVES_TABLE,
} from "../../constants/FirestoreConstant";
import { WAVE_STATUS } from "../../constants/PickingConstant";
import {
  buildPickingHistoryEntries,
  buildPickingUpdate,
  pickingUpdateChangesOrder,
  projectPickEvents,
} from "../../functions/pickEvents";
import { splitOrderRef } from "../../functions/waveDocument";
import { resolveFirestore } from "./shopFirestore";
import { hasHvoSession } from "./useProductFields";

/**
 * From the scanners' pick events back onto the orders (HVC-51), for both shops.
 *
 * The app writes events to `pickWaves/{waveId}/events` in VCE; only the CMS has a session with
 * both projects, so the CMS projects them onto each order in its own shop. The projection is the
 * pure fold in functions/pickEvents over ALL events of a wave - re-running it is harmless - and an
 * order is only written when its picking state actually changes, so a page that projects every
 * time it sees new events causes no churn and no duplicate history.
 *
 * Nothing here ever writes `orderStatus`: that is the shipping exports' field.
 */

const wavesCollection = () => vceFirestore.collection(FIRESTORE_PICK_WAVES_TABLE);

export const fetchWaveEvents = async (waveId) => {
  const snapshot = await wavesCollection().doc(waveId).collection(FIRESTORE_PICK_EVENTS_SUBCOLLECTION).get();
  return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
};

const fetchEventsForWaves = async (waveIds) => {
  const entries = await Promise.all(waveIds.map(async (waveId) => [waveId, await fetchWaveEvents(waveId)]));
  return Object.fromEntries(entries);
};

const currentEmail = () => (auth.currentUser && auth.currentUser.email) || null;
const currentUid = () => (auth.currentUser && auth.currentUser.uid) || null;

const NO_EVENTS = {};

/**
 * @param {Object[]} waves - the waves of one pick day (usePickWaves)
 * @returns {{ projections: Object<waveId, projection>, projectWave, isLoading, refresh }}
 */
export const usePickResults = (waves) => {
  const { mutate: globalMutate } = useSWRConfig();
  const waveIds = useMemo(
    () => (waves || []).filter((wave) => wave.status !== WAVE_STATUS.CANCELLED).map((wave) => wave.waveId || wave.id),
    [waves],
  );
  const { data, isLoading, mutate } = useSWR(
    waveIds.length > 0 ? ["pickEvents", ...waveIds] : null,
    () => fetchEventsForWaves(waveIds),
    { refreshInterval: 20000, revalidateOnFocus: true, dedupingInterval: 5000 },
  );
  const eventsByWave = data || NO_EVENTS;

  const projections = useMemo(
    () =>
      Object.fromEntries(
        (waves || [])
          .filter((wave) => !!eventsByWave[wave.waveId || wave.id])
          .map((wave) => [wave.waveId || wave.id, projectPickEvents(wave, eventsByWave[wave.waveId || wave.id])]),
      ),
    [waves, eventsByWave],
  );

  /**
   * Write one wave's projection onto its orders. Returns per order what happened, failures first
   * for the screen; an HVO order without an HVO session is reported, not thrown.
   */
  const projectWave = useCallback(
    async (wave) => {
      const waveId = wave.waveId || wave.id;
      const events = await fetchWaveEvents(waveId);
      const projection = projectPickEvents(wave, events);
      const now = Date.now();
      const results = [];

      for (const waveOrder of wave.orders || []) {
        const { shopSource, docId } = splitOrderRef(waveOrder.orderRef);
        const state = projection.orders[waveOrder.orderRef];
        if (shopSource === "HVO" && !hasHvoSession()) {
          results.push({ orderRef: waveOrder.orderRef, orderId: waveOrder.orderId, ok: false, reason: "no-hvo-session" });
          continue;
        }
        try {
          const firestore = resolveFirestore(shopSource);
          const reference = firestore.collection(FIRESTORE_ORDERS_TABLE).doc(docId);
          const snapshot = await reference.get();
          if (!snapshot.exists) {
            results.push({ orderRef: waveOrder.orderRef, orderId: waveOrder.orderId, ok: false, reason: "order-missing" });
            continue;
          }
          const current = snapshot.data().picking || null;
          // An order taken out of this wave (cancelled, or put in another) is not ours to write
          if (!!current && !!current.waveId && current.waveId !== waveId) {
            results.push({ orderRef: waveOrder.orderRef, orderId: waveOrder.orderId, ok: false, reason: "other-wave" });
            continue;
          }
          const update = buildPickingUpdate(state, wave, waveOrder, now);
          if (!pickingUpdateChangesOrder(current, update)) {
            results.push({ orderRef: waveOrder.orderRef, orderId: waveOrder.orderId, ok: true, status: state.status, changed: false });
            continue;
          }
          const batch = firestore.batch();
          batch.update(reference, update);
          buildPickingHistoryEntries(current, state, wave).forEach((entry) =>
            batch.set(reference.collection(FIRESTORE_HISTORY_SUBCOLLECTION).doc(), {
              timestamp: now,
              adminEmail: currentEmail(),
              adminUid: currentUid(),
              ...entry,
            }),
          );
          await batch.commit();
          results.push({ orderRef: waveOrder.orderRef, orderId: waveOrder.orderId, ok: true, status: state.status, changed: true });
        } catch (error) {
          results.push({ orderRef: waveOrder.orderRef, orderId: waveOrder.orderId, ok: false, reason: "write-failed", message: error.message });
        }
      }

      const counts = Object.values(projection.orders).reduce((total, state) => ({ ...total, [state.status]: (total[state.status] || 0) + 1 }), {});
      const waveUpdate = {
        projection: { at: now, eventCount: projection.eventCount, lastEventId: projection.lastEventId, status: projection.waveStatus, counts },
        updatedAt: now,
      };
      // The app sets busy/done itself when it syncs; the fold makes it true even when it has not yet
      if (projection.waveStatus === WAVE_STATUS.DONE && wave.status !== WAVE_STATUS.DONE) {
        waveUpdate.status = WAVE_STATUS.DONE;
        if (!wave.finishedAt) waveUpdate.finishedAt = now;
      } else if (projection.waveStatus === WAVE_STATUS.BUSY && wave.status === WAVE_STATUS.OPEN) {
        waveUpdate.status = WAVE_STATUS.BUSY;
      }
      await wavesCollection().doc(waveId).update(waveUpdate);

      await mutate();
      await globalMutate((key) => Array.isArray(key) && ["orders", "pickDayOrders", "pickWaves"].includes(key[0]));
      return {
        ok: results.every((result) => result.ok),
        results: [...results].sort((a, b) => Number(a.ok) - Number(b.ok)),
        projection,
      };
    },
    [mutate, globalMutate],
  );

  return { projections, eventsByWave, projectWave, isLoading, refresh: mutate };
};
